import { useEffect, useState, useRef } from 'react';
import { helpSession } from '@openclassrooms/common-app/contact/services';
type BasicPage = {
  type: 'main' | 'phone' | 'email';
  data: never;
};
type EmailPage = {
  type: 'email';
  data?: {
    ownerEmail?: string;
  };
};
type CompanionPage = {
  type: 'companion';
  data: {
    initialMessage?: string;
  };
};
export type CurrentPage = BasicPage | EmailPage | CompanionPage;
export type PageType = CurrentPage['type'];
export default function useDialog({
  isOpenDefault = false,
  onEmailHashClick
}: {
  isOpenDefault?: boolean;
  onEmailHashClick?: () => void;
} = {}) {
  const [isDialogOpen, setIsDialogOpen] = useState(isOpenDefault);
  const [currentPage, setCurrentPage] = useState<CurrentPage | undefined>(isOpenDefault ? {
    type: 'main',
    data: undefined as never
  } : undefined);
  const isForcedOpenPage = useRef(false);
  const onEmailHashClickRef = useRef(onEmailHashClick);
  onEmailHashClickRef.current = onEmailHashClick;
  const shouldDisplayBack = currentPage?.type !== 'main' && !isForcedOpenPage.current;
  useEffect(() => {
    const handleContactButtonClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) {
        return;
      }
      const link = target.closest('a');
      if (!link || link.href === '') {
        return;
      }
      const {
        hash
      } = new URL(link.href);
      if (document.querySelector('.MuiModal-root')) {
        return;
      }
      if (hash === '#contact-email') {
        e.preventDefault();
        onEmailHashClickRef.current?.();
      } else if (hash === '#contact') {
        e.preventDefault();
        setIsDialogOpen(true);
        setCurrentPage({
          type: 'main',
          data: undefined as never
        });
      }
    };
    document.addEventListener('click', handleContactButtonClick);
    return () => {
      document.removeEventListener('click', handleContactButtonClick);
    };
  }, []);
  const goMainPage = () => {
    setCurrentPage({
      type: 'main',
      data: undefined as never
    });
  };
  const openEmail = (ownerEmail?: string, {
    forced = false
  }: {
    forced?: boolean;
  } = {}) => {
    setCurrentPage({
      type: 'email',
      data: {
        ownerEmail
      }
    });
    isForcedOpenPage.current = forced;
    setIsDialogOpen(true);
  };
  const open = async (forcePage?: CurrentPage) => {
    if (forcePage) {
      setCurrentPage(forcePage);
      isForcedOpenPage.current = true;
    } else {
      const threadId = await helpSession.getThreadId();
      setCurrentPage(threadId ? {
        type: 'companion',
        data: {
          initialMessage: ''
        }
      } : {
        type: 'main',
        data: undefined as never
      });
    }
    setIsDialogOpen(true);
  };
  const close = (cause?: string) => {
    if (currentPage?.type !== 'main' && cause === 'backbuttonClick') {
      setCurrentPage({
        type: 'main',
        data: undefined as never
      });
      return;
    }
    setIsDialogOpen(false);
    setCurrentPage({
      type: 'main',
      data: undefined as never
    });
    isForcedOpenPage.current = false;
  };
  return {
    currentPage,
    isDialogOpen,
    shouldDisplayBack,
    changePage: setCurrentPage,
    goMainPage,
    open,
    openEmail,
    close
  };
}